/* eslint-disable @typescript-eslint/no-empty-object-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */
// 2) Third-party libraries
import { get, isEqual, uniqWith, keyBy, merge, values } from "lodash-es";
import {
    createSlice,
    PayloadAction,
    createAction,
    createSelector,
} from "@reduxjs/toolkit";

// 3) Local imports
// 3.1 Constants
import { DETAILS_TAB, SALES_ORDERS_PAGE } from "../../config/tableAndPageConstants";
import {
    salesOrdersChangesTableFilterOptions,
    salesOrdersSortByOptions,
    salesOrdersStateFilterOptions,
} from "../../constants/salesOrdersSortAndFilters";
import { conversationTableFilterOptions } from "../../constants/common";

// 3.3 Project-specific modules � components/types
import {
    DeleteSalesOrderManuallyRequestPayload,
    GetSalesOrderChangesRequestPayload,
    GetSalesOrdersRequestPayload,
    RequestCreateSalesOrderManuallyPayload,
    RequestEditSalesOrderManuallyPayload,
    SalesOrder,
    SalesOrderAddCommentRequestPayload,
    SalesOrderChanges,
    SalesOrdersActionTypes,
    SalesOrdersState,
} from "./types";
import { Conversation, GetConversationRequestPayload, PageData } from "../common/types";

// 3.4 Utility functions / types
import { DynamicObject } from "../../utils/commonInterfaces";

// 3.5 Redux actions / store
import type { RootState } from "../storeTypes";

const actionPrefix = 'salesOrders';

// Type-safe initialState!
export const initialState: SalesOrdersState = {
    loading: false,
    errorMessages: [],
    data: [],
    pageData: {
        pageSize: SALES_ORDERS_PAGE.pageSize,
        currentPage: 0,
        hasNextPage: false,
    },
    filters: {
        InvoiceNumber: '',
        SalesOrderNumber: '',
        Customer: '',
        CustomerCode: '',
        CreatedDateMin: '',
        CreatedDateMax: '',
        DueDateMin: '',
        DueDateMax: '',
        AmountType: '',
        AmountValue: '',
        AmountOperator: '',
    },
    tableFilter: undefined,
    salesOrderState: get(salesOrdersStateFilterOptions, `${0}.value`),
    sortBy: salesOrdersSortByOptions[0].value,
    sortAscending: true,
    activeData: {
        record: {},
        loading: false,
        selectedId: null,
        errorMessages: [],
        conversation: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.CONVERSATION_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            conversationTableFilter: get(
                conversationTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
        salesOrderChanges: {
            loading: false,
            errorMessages: [],
            data: [],
            pageData: {
                pageSize: DETAILS_TAB.SALES_ORDER_CHANGES_TIMELINE.pageSize,
                currentPage: 0,
                hasNextPage: false,
            },
            filters: {},
            salesOrderChangesTableFilter: get(
                salesOrdersChangesTableFilterOptions,
                '0.value'
            ),
            sortBy: 'Created Date Time',
            sortAscending: false,
        },
    },
    compactView: false,
};

// Thanks to Redux 4's much simpler typings, we can take away a lot of typings on the reducer side,
// everything will remain type-safe.
const salesOrdersSlice = createSlice({
    name: actionPrefix,
    initialState,
    reducers: {
        getSalesOrdersRequestAction: (
            state,
            _action: PayloadAction<GetSalesOrdersRequestPayload>
        ) => {
            state.loading = true;
        },
        getSalesOrdersForOrganisationRequestAction: (
            state,
            _action: PayloadAction<GetSalesOrdersRequestPayload>
        ) => {
            state.loading = true;
        },
        getSalesOrdersSuccessAction: (
            state,
            action: PayloadAction<{
                data: SalesOrder[];
                pageData: PageData;
                mergeData?: boolean;
            }>
        ) => {
            const { data, pageData, mergeData } = action.payload;

            if (mergeData === true) {
                state.data = values(
                    merge(
                        keyBy(state.data, 'Id'),
                        keyBy(data, 'Id')
                    )
                );
            } else {
                if (pageData.currentPage === 0) {
                    state.data = data;
                } else {
                    const existingData = state.data || [];
                    state.data = uniqWith([...existingData, ...data], isEqual);
                }
            }

            state.loading = false;
            state.pageData = pageData;
            state.errorMessages = [];
        },
        getSalesOrdersErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.loading = false;
            state.data = [];
            state.errorMessages = action.payload;
        },
        updateSalesOrdersFiltersAction: (
            state,
            action: PayloadAction<GetSalesOrdersRequestPayload['filters']>
        ) => {
           state.filters = action.payload
        },
        updateSalesOrdersSortByAndStateAction: (
            state,
            action: PayloadAction<{
                sortBy: GetSalesOrdersRequestPayload['sortBy'];
                sortAscending: boolean;
                salesOrderState: string;
            }>
        ) => {
            state.sortBy = action.payload.sortBy;
            state.sortAscending = action.payload.sortAscending;
            state.salesOrderState = action.payload.salesOrderState;
        },
        updateSalesOrdersTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.tableFilter = action.payload;
        },
        clearSalesOrdersStateAllTableFiltersAction: (state) => {
            state.filters = { ...initialState.filters };
            state.sortBy = initialState.sortBy;
            state.sortAscending = initialState.sortAscending;
            state.salesOrderState = initialState.salesOrderState;
            state.tableFilter = initialState.tableFilter;
        },
        clearSalesOrdersStateDataAction: (state) => {
            Object.assign(state, initialState);
        },
        clearSalesOrdersListData: (state) => {
            state.loading = false;
            state.data = [];
            state.pageData = initialState.pageData;
            state.errorMessages = [];
        },
        setSelectedSalesOrderIdRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{
                    salesOrderId: string | null,
                    callback: () => void
                }>
            ) => {
                state.activeData.selectedId = initialState.activeData.selectedId;
            },
            prepare: (
                salesOrderId: string | null,
                callback: () => void
            ) => ({
                payload: { salesOrderId, callback }
            })
        },
        setSelectedSalesOrderIdSuccessAction: (
            state,
            action: PayloadAction<string | null>
        ) => {
            state.activeData.selectedId = action.payload;
        },
        getSalesOrderDataRequestAction: {
            reducer: (
                state,
                _action: PayloadAction<{ salesOrderId: string }>
            ) => {
                state.activeData.record = { ...initialState.activeData.record };
                state.activeData.loading = true;
            },
            prepare: (
                salesOrderId: string
            ) => {
                return {
                    payload: {
                        salesOrderId
                    }
                }
            }
        },
        getOrganisationSalesOrderDataRequestAction: (
            state,
            _action: PayloadAction<{
                salesOrderId: string,
                region: string,
                isUsingCloudImportType: boolean
            }>
        ) => {
            state.activeData.record = initialState.activeData.record;
            state.activeData.loading = true;
        },
        getSalesOrderDataSuccessAction: (
            state,
            action: PayloadAction<{ record: {} }>
        ) => {
            state.activeData.record = action.payload.record;
            state.activeData.loading = false;
            state.activeData.errorMessages = [];
        },
        getSalesOrderDataErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.record = { ...initialState.activeData.record };
            state.activeData.loading = false;
            state.activeData.errorMessages = action.payload;

        },
        clearSalesOrderDataAction: (state) => {
            state.activeData = initialState.activeData;
        },
        getSalesOrderConversationRequestAction: (
            state,
            _action: PayloadAction<GetConversationRequestPayload>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getOrganisationSalesOrderConversationRequestAction: (
            state,
            _action: PayloadAction<{
                payload: GetConversationRequestPayload,
                region: string
            }>
        ) => {
            state.activeData.conversation.loading = true;
        },
        getSalesOrderConversationSuccessAction: (
            state,
            action: PayloadAction<{
                data: Conversation[];
                pageData: PageData;
            }>
        ) => {
            if (action.payload.pageData.currentPage === 0) {
                state.activeData.conversation.data = action.payload.data;
            }
            else {
                state.activeData.conversation.data = uniqWith(
                    [...state.activeData.conversation.data, ...action.payload.data],
                    isEqual
                );
            }

            state.activeData.conversation.loading = false;
            state.activeData.conversation.pageData = action.payload.pageData;
            state.activeData.conversation.errorMessages = [];
        },
        getSalesOrderConversationErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.conversation.loading = false;
            state.activeData.conversation.data = { ...initialState.activeData.conversation.data };
            state.activeData.conversation.errorMessages = action.payload;
        },
        updateSalesOrderConversationFiltersAction: (
            state,
            action: PayloadAction<GetConversationRequestPayload['filters']>
        ) => {
            state.activeData.conversation.filters = action.payload;
        },
        updateSalesOrderConversationTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.conversation.conversationTableFilter = action.payload;
        },
        updateSalesOrderConversationSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetConversationRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.conversation.sortBy = action.payload.sortBy;
            state.activeData.conversation.sortAscending = action.payload.sortAscending;
        },
        getSalesOrderChangesRequestAction: (
            state,
            _action: PayloadAction<GetSalesOrderChangesRequestPayload>
        ) => {
            state.activeData.salesOrderChanges.loading = true;
        },
        getOrganisationSalesOrderChangesRequestAction: (
            state,
            _action: PayloadAction<{
                payload: GetSalesOrderChangesRequestPayload;
                region: string;
            }>
        ) => {
            state.activeData.salesOrderChanges.loading = true;
        },
        getSalesOrderChangesSuccessAction: (
            state,
            action: PayloadAction<{
                data: SalesOrder[];
                pageData: PageData;
            }>
        ) => {
            const { data, pageData } = action.payload;

            if (pageData.currentPage === 0) {
                state.activeData.salesOrderChanges.data = data;
            } else {
                state.activeData.salesOrderChanges.data = uniqWith(
                    [...state.activeData.salesOrderChanges.data, ...data],
                    isEqual
                );
            }

            state.activeData.salesOrderChanges.loading = false;
            state.activeData.salesOrderChanges.pageData = pageData;
            state.activeData.salesOrderChanges.errorMessages = [];
        },
        getSalesOrderChangesErrorAction: (
            state,
            action: PayloadAction<string[]>
        ) => {
            state.activeData.salesOrderChanges.loading = false;
            state.activeData.salesOrderChanges.data = [];
            state.activeData.salesOrderChanges.errorMessages = action.payload;
        },
        updateSalesOrderChangesFiltersAction: (
            state,
            action: PayloadAction<GetSalesOrderChangesRequestPayload['filters']>
        ) => {
            state.activeData.salesOrderChanges.filters = action.payload;
        },
        updateSalesOrderChangesTableFilterAction: (
            state,
            action: PayloadAction<string>
        ) => {
            state.activeData.salesOrderChanges.salesOrderChangesTableFilter = action.payload;
        },
        updateSalesOrderChangesSortByAction: (
            state,
            action: PayloadAction<{
                sortBy: GetSalesOrderChangesRequestPayload['sortBy'];
                sortAscending: boolean;
            }>
        ) => {
            state.activeData.salesOrderChanges.sortBy = action.payload.sortBy;
            state.activeData.salesOrderChanges.sortAscending = action.payload.sortAscending;
        },
        setTableCompactViewAction: (
            state,
            action: PayloadAction<boolean>
        ) => {
            state.compactView = action.payload;
        },
    }
});

const { actions, reducer } = salesOrdersSlice;

// Actions
export const {
    getSalesOrdersRequestAction,
    getSalesOrdersForOrganisationRequestAction,
    getSalesOrdersSuccessAction,
    getSalesOrdersErrorAction,
    updateSalesOrdersFiltersAction,
    updateSalesOrdersSortByAndStateAction,
    updateSalesOrdersTableFilterAction,
    clearSalesOrdersStateAllTableFiltersAction,
    clearSalesOrdersStateDataAction,
    clearSalesOrdersListData,
    setSelectedSalesOrderIdRequestAction,
    setSelectedSalesOrderIdSuccessAction,
    getSalesOrderDataRequestAction,
    getOrganisationSalesOrderDataRequestAction,
    getSalesOrderDataSuccessAction,
    getSalesOrderDataErrorAction,
    clearSalesOrderDataAction,
    getSalesOrderConversationRequestAction,
    getOrganisationSalesOrderConversationRequestAction,
    getSalesOrderConversationSuccessAction,
    getSalesOrderConversationErrorAction,
    updateSalesOrderConversationFiltersAction,
    updateSalesOrderConversationTableFilterAction,
    updateSalesOrderConversationSortByAction,
    getSalesOrderChangesRequestAction,
    getOrganisationSalesOrderChangesRequestAction,
    getSalesOrderChangesSuccessAction,
    getSalesOrderChangesErrorAction,
    setTableCompactViewAction,
    updateSalesOrderChangesFiltersAction,
    updateSalesOrderChangesSortByAction,
    updateSalesOrderChangesTableFilterAction
} = actions;

export const salesOrderAddCommentRequestAction = createAction(
    `${actionPrefix}/salesOrderAddCommentRequestAction`,
    function prepare(payload: SalesOrderAddCommentRequestPayload) {
        return {
            payload
        };
    }
);

export const organisationSalesOrderAddCommentRequestAction = createAction(
    `${actionPrefix}/organisationSalesOrderAddCommentRequestAction`,
    function prepare(
        payload: SalesOrderAddCommentRequestPayload,
        region: string
    ) {
        return {
            payload: {
                payload,
                region
            }
        };
    }
);

export const getSalesOrderCustomFieldsForCompanyRequestAction = createAction(
    `${actionPrefix}/getSalesOrderCustomFieldsForCompanyRequestAction`,
    function prepare(
        companyId: string,
        callback?: (response: DynamicObject) => void
    ) {
        return {
            payload: {
                companyId,
                callback
            }
        };
    }
);

export const editSalesOrderManuallyRequestAction = createAction(
    `${actionPrefix}/editSalesOrderManuallyRequestAction`,
    function prepare(
        payload: RequestEditSalesOrderManuallyPayload
    ) {
        return {
            payload
        };
    }
);

export const deleteSalesOrderManuallyRequestAction = createAction(
    `${actionPrefix}/deleteSalesOrderManuallyRequestAction`,
    function prepare(
        payload: DeleteSalesOrderManuallyRequestPayload
    ) {
        return {
            payload
        };
    }
);

export const getSalesOrdersBySalesOrderCodesRequestAction = createAction(
    `${actionPrefix}/getSalesOrdersBySalesOrderCodesRequestAction`,
    function prepare(
        payload: any,
        callback?: Function
    ) {
        return {
            payload: {
                payload,
                callback
            }
        };
    }
);

export const getSalesOrdersBySalesOrderCodesSuccessAction = createAction(
    `${actionPrefix}/getSalesOrdersBySalesOrderCodesSuccessAction`,
    function prepare(
        data: SalesOrder[]
    ) {
        return {
            payload: {
                data
            }
        };
    }
);

export const getSalesOrdersBySalesOrderCodesErrorAction = createAction(
    `${actionPrefix}/getSalesOrdersBySalesOrderCodesSuccessAction`,
    function prepare(
        payload: string[]
    ) {
        return {
            payload
        };
    }
);

export const createSalesOrderManuallyRequestAction = createAction(
    `${actionPrefix}/createSalesOrderManuallyRequestAction`,
    function prepare(
        payload: RequestCreateSalesOrderManuallyPayload
    ) {
        return {
            payload
        };
    }
);

// Selectors
export const getSalesOrdersState = (state: RootState) => state.salesOrders;
export const getSalesOrderData = createSelector(
    getSalesOrdersState,
    (salesOrders) => salesOrders.activeData
);
export const getSelectedSalesOrderId = createSelector(
    getSalesOrderData,
    (activeData) => {
        return activeData.selectedId;
    }
);
export const getSalesOrdersPageData = createSelector(
    getSalesOrdersState,
    (salesOrders) => salesOrders.pageData
);

// Reducer
export default reducer;